import React from 'react';

/**
 * Web screen registry: manifest screen key -> component.
 *
 * Joined to the shared manifest in `@tenant/core/routes` by slug (or by the
 * manifest's explicit `screen` field where one path resolves to several
 * screens, as `home` does). A key absent from this map simply does not mount.
 *
 * Keep the keys in sync with the slugs in
 * `packages/core/routes/manifest.js`.
 */

/** Authenticated */
const PropertyListFeature = React.lazy(() =>
  import('../features/property/list-feature')
);

const PropertyDetailFeature = React.lazy(() =>
  import('../features/property/detail-feature')
);

const ViewingListFeature = React.lazy(() =>
  import('../features/viewing/list-feature')
);

const ViewingDetailFeature = React.lazy(() =>
  import('../features/viewing/detail-feature')
);

const ApplicationsListFeature = React.lazy(() =>
  import('../features/application/list-feature')
);

const ApplicationDetailsFeature = React.lazy(() =>
  import('../features/application/details-feature')
);

const ApplicationApplicantDetailsFeature = React.lazy(() =>
  import('../features/application/applicant-details-feature')
);

const ApplicationStage2Feature = React.lazy(() =>
  import('../features/application/stage-2-feature')
);

const SectionApplicationSuccess = React.lazy(() =>
  import('../components/SectionApplicationSuccess')
);

const AgreementListFeature = React.lazy(() =>
  import('../features/agreement/list-feature')
);

const AgreementDetailFeature = React.lazy(() =>
  import('../features/agreement/detail-feature')
);

const AgreementDetailFormFeature = React.lazy(() =>
  import('../features/agreement/detail-form-feature')
);

const ProfileFeature = React.lazy(() =>
  import('../features/profile/tabs-feature')
);

const ProfileUpgradeContainer = React.lazy(() =>
  import('../containers/ProfileUpgradeContainer')
);

const ProfileCreditReportsContainer = React.lazy(() =>
  import('../containers/ProfileCreditReportsContainer')
);

const ProfileUpgradeVerifyContainer = React.lazy(() =>
  import('../containers/ProfileUpgradeVerifyContainer')
);

const SecurityContainer = React.lazy(() =>
  import('../containers/SecurityContainer')
);

const VerifyFeature = React.lazy(() =>
  import('../features/user/verify-feature')
);

const TwoFactorEnrolFeature = React.lazy(() =>
  import('../features/user/two-factor-enrol-feature')
);

const NonVerifiedFeature = React.lazy(() =>
  import('../features/home/non-verified-feature')
);

const WelcomeFeature = React.lazy(() =>
  import('../features/home/welcome-feature')
);

/** Unauthenticated */
const LoginContainer = React.lazy(() =>
  import('../containers/LoginContainer/LoginContainer')
);

const TwoFactorLoginFeature = React.lazy(() =>
  import('../features/user/two-factor-login-feature')
);

const RegisterFeature = React.lazy(() =>
  import('../features/register/RegisterFeature')
);

const ForgotPasswordFeature = React.lazy(() =>
  import('../features/reset/ForgotPasswordFeature')
);

const PasswordResetVerifyFeature = React.lazy(() =>
  import('../features/reset/PasswordResetVerifyFeature')
);

const PasswordResetFeature = React.lazy(() =>
  import('../features/reset/PasswordResetFeature')
);

/** Public */
const AboutFeature = React.lazy(() => import('../features/about/AboutFeature'));

const ContactFeature = React.lazy(() =>
  import('../features/contact/contact-feature')
);

const HomeContent = React.lazy(() => import('../components/HomeContent'));

const PrivacyFeature = React.lazy(() =>
  import('../features/privacy/privacy-feature')
);

const PrivacyPolicyFeature = React.lazy(() =>
  import('../features/privacy/privacy-policy-feature')
);

const RequestDataFeature = React.lazy(() =>
  import('../features/requestdata/request-data-feature')
);

const NotFound = React.lazy(() => import('../components/NotFound'));

const screens = {
  /** Authenticated */
  home_non_verified: NonVerifiedFeature,
  home_welcome: WelcomeFeature,
  properties: PropertyListFeature,
  'property-details': PropertyDetailFeature,
  viewings: ViewingListFeature,
  viewing_details: ViewingDetailFeature,
  applications: ApplicationsListFeature,
  application_complete: SectionApplicationSuccess,
  application_overview: ApplicationDetailsFeature,
  application_view: ApplicationApplicantDetailsFeature,
  application_submit: ApplicationStage2Feature,
  agreements: AgreementListFeature,
  agreement_view: AgreementDetailFeature,
  agreement_sign: AgreementDetailFormFeature,
  profile: ProfileFeature,
  profile_my_data: ProfileUpgradeContainer,
  credit_reports: ProfileCreditReportsContainer,
  profile_upgrade_types: ProfileUpgradeVerifyContainer,
  settings: SecurityContainer,
  verify_account: VerifyFeature,
  login_enrol: TwoFactorEnrolFeature,

  /** Unauthenticated */
  login: LoginContainer,
  login_verify: TwoFactorLoginFeature,
  register: RegisterFeature,
  request_reset: ForgotPasswordFeature,
  verify_reset: PasswordResetVerifyFeature,
  reset: PasswordResetFeature,

  /** Public */
  public_home: HomeContent,
  about: AboutFeature,
  contact: ContactFeature,
  privacy: PrivacyFeature,
  privacy_policy: PrivacyPolicyFeature,
  request_data: RequestDataFeature,
  not_found: NotFound,
};

export default screens;
